import {
  DEFAULT_FORM_FIELD_ATTRIBUTE,
  VALIDATION_ERROR_NAVIGATION_DELAY_MS,
} from "./constants";

export interface ValidationFieldMeta {
  name: string;
  label: string;
  groupName?: string;
}

export interface ValidationErrorItem extends ValidationFieldMeta {
  message: string;
}

export interface FocusFormFieldOptions {
  fieldAttribute?: string;
  delay?: number;
}

const FOCUS_TARGET_SELECTOR = [
  "input:not([disabled])",
  "textarea:not([disabled])",
  ".k-dropdownlist:not(.k-disabled)",
  ".k-picker:not(.k-disabled)",
  "[tabindex]:not([tabindex='-1'])",
  "button:not([disabled])",
].join(", ");

const datasetKeyFromAttribute = (fieldAttribute: string): string =>
  fieldAttribute
    .replace(/^data-/, "")
    .replace(/-([a-z])/g, (_, char: string) => char.toUpperCase());

export const getFormFieldElement = (
  fieldName: string,
  fieldAttribute = DEFAULT_FORM_FIELD_ATTRIBUTE,
): HTMLElement | null => {
  const fields = document.querySelectorAll<HTMLElement>(`[${fieldAttribute}]`);
  const datasetKey = datasetKeyFromAttribute(fieldAttribute);

  return (
    Array.from(fields).find(
      item => item.dataset[datasetKey as keyof DOMStringMap] === fieldName,
    ) ?? null
  );
};

export const focusFormField = (
  fieldName: string,
  {
    fieldAttribute = DEFAULT_FORM_FIELD_ATTRIBUTE,
    delay = VALIDATION_ERROR_NAVIGATION_DELAY_MS,
  }: FocusFormFieldOptions = {},
): void => {
  const fieldElement = getFormFieldElement(fieldName, fieldAttribute);
  if (!fieldElement) return;

  fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });

  const focusTarget = fieldElement.querySelector<HTMLElement>(
    FOCUS_TARGET_SELECTOR,
  );

  window.setTimeout(() => {
    (focusTarget ?? fieldElement).focus({ preventScroll: true });
  }, delay);
};

export const buildValidationErrorItems = (
  requiredFields: ValidationFieldMeta[],
  errors: Record<string, string | undefined>,
): ValidationErrorItem[] =>
  requiredFields.reduce<ValidationErrorItem[]>((items, field) => {
    const message = errors[field.name];
    if (!message) return items;
    return [...items, { ...field, message }];
  }, []);
